import { Suspense } from 'react';
import { transportLayersEndpoints } from '@ooh/contracts';
import { useApiSuspenseQuery } from '../data/use-api-suspense-query';
import { ErrorBoundary } from '../layouts/error-boundary';

const MAX_ITEMS = 2;

export interface TransportNearbySectionProps {
  mediaUnitId: number;
  busEnabled: boolean;
  roadsEnabled: boolean;
  nearbyTitle: string;
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1).replace('.', ',')} km` : `${meters} m`;
}

function NearbyTransportItems({
  mediaUnitId,
  busEnabled,
  roadsEnabled,
  nearbyTitle,
}: TransportNearbySectionProps) {
  const { data } = useApiSuspenseQuery(transportLayersEndpoints.nearbyTransport, {
    id: mediaUnitId,
  });

  const items = data.data
    .filter((item) => (item.scope === 'public' ? busEnabled : roadsEnabled))
    .slice(0, MAX_ITEMS);

  if (items.length === 0) return null;

  return (
    <div className="mt-3 border-t border-border/50 pt-2.5">
      <p className="text-2xs font-semibold text-foreground mb-1.5">{nearbyTitle}</p>
      <div className="space-y-1">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between text-2xs">
            <span className="text-muted-foreground">{item.label}</span>
            <span className="font-medium text-foreground tabular-nums">
              {formatDistance(item.distanceMeters)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// A popup extra: while loading or after a failed request it shows nothing instead of a skeleton or retry.
export function TransportNearbySection(props: TransportNearbySectionProps) {
  if (!props.busEnabled && !props.roadsEnabled) return null;

  return (
    <ErrorBoundary onReset={() => undefined} renderError={() => null}>
      <Suspense fallback={null}>
        <NearbyTransportItems {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}
